import React, { useContext, useMemo, useState } from 'react';
import { useIntl, getLocale, useSelector } from 'umi';
import { Card } from 'tea-component';
import classNames from 'classnames';
import { get } from 'lodash-es';
import MetricCardItem from '.';
import MetricCardChartItem from './MetricCardChartItem';
import PlatformCardItem from './PlatformCardItem';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import MetricCardTitle from './MetricCardTitle';
import { getExplainVal } from '../../utils/getExplainVal';
import { formatSeries, getDecimalFormateStr } from '../MetricCard/utils';
import '@/pages/dashboard/assets/scss/page/Overlay/components/cartContext.scss';
import { jumpDetail } from '../../utils/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import ACard from '../ACard';
import VsBenchmarkBtn from './components/VsBenchmarkBtn';
import { dynamicString } from '@home/utils/dynamicString';
import { getLanguage, getName } from '@home/utils/common';
import moment from 'moment';
import { getDataByKey } from '../TrendChartCo/_utils/common';
import { IS_GLOBAL } from '@home/_config/config';
import { buildJumpDetailFormValue, DATE_TYPE_TO_TIME_TYPE } from './_utils';

export default function PlatformCard(props: any) {
  const { config, isChange, data, benchmarkConfig = {}, hasVs: propsHasVs, dimension = "os", defaultSelectedList,  hasChart = true, showShare = true, style, className, isRealTime= false, breakdownTooltipMap: breakdownTooltipMapProp, ...restProps } = props;
  const {
    componentMetrics = [],
    jumpUrl,
    jumpType,
    hasVs,
    dataKey: apiDataKey,
    breakdownIconConfig,
    financeJumpUrlsByGranularity,
    financeJumpUrlsByGranularityGlobal,
    breakdownTooltipMap: breakdownTooltipMapConfig,
  } = config;
  // 优先级：data.catalogTooltipMap（页面级共享配置，贯穿所有组件） > config.breakdownTooltipMap > props.breakdownTooltipMap
  const breakdownTooltipMap = data?.catalogTooltipMap ?? breakdownTooltipMapConfig ?? breakdownTooltipMapProp;
  const { metricsList, isDark = false } = data || {};
  const effectiveMetricsList = metricsList || componentMetrics?.map((m: any) => m?.metricKey);
  const dataKey = componentMetrics?.[0]?.metricKey || '';
  const { formatMessage } = useIntl();
  const contextObj = useContext(FilterContext);
  // 筛选器降级策略：优先使用 FilterContext 中的筛选值；
  // 当无 FilterContext 且配置了 breakdownIconConfig 时，回退到 data.filter（由外部服务注入的筛选快照）
  const { filterVal: contextFilterVal } = contextObj || {};
  const hasContextFilter = Object.keys(contextFilterVal || {}).length > 0;
  let effectiveFilterVal = contextFilterVal;
  if (hasContextFilter) {
    effectiveFilterVal = contextFilterVal;
  } else if (breakdownIconConfig) {
    effectiveFilterVal = data?.filter;
  } else {
    effectiveFilterVal = data?.filter || contextFilterVal;
  }
  const timeType = effectiveFilterVal?.timeType || DATE_TYPE_TO_TIME_TYPE[effectiveFilterVal?.date_type] || 'Day';
  const filterVal = effectiveFilterVal || {};
  const { explainInfo, gameCode } = useSelector((state: any) => ({
    explainInfo: state.dashboard?.explainInfo,
    gameCode: state?.dashboard?.currentGame?.game_code,
  }));
  const [isClick] = useState(false);
  const isEN = getLocale() === 'en-US';

  // keyMetricsData是旧结构，添加了dataKey后，需要使用getDataByKey获取data传入的数据，dataKey可以是动态字符串格式，如{{cpi.data}}
  const { data: keyMetricsData } = data?.keyMetricsData || {};
  const { card_list: metricsData = [], trend_list: keyMetricsTrend = [], breakdown: platFormData = [] } = apiDataKey ? (getDataByKey(apiDataKey, data) || {}) : keyMetricsData?.[dataKey] || {};

  const gameList = useMemo(() => {
    if (!breakdownIconConfig) return [];
    const path = breakdownIconConfig.gameListDataPath || 'studioData.game_list';
    return get(data, path, []);
  }, [data, breakdownIconConfig]);

  const resolvedJumpUrl = useMemo(() => {
    if (financeJumpUrlsByGranularity && typeof financeJumpUrlsByGranularity === 'object') {
      const map =
        IS_GLOBAL && financeJumpUrlsByGranularityGlobal
          ? financeJumpUrlsByGranularityGlobal
          : financeJumpUrlsByGranularity;
      const key = (filterVal?.date_type as string) || (data?.filter?.date_type as string) || 'daily';
      const url = map[key] || map.daily;
      if (url) return url;
    }

    if (!jumpUrl || jumpUrl === 'none') return '';
    const fromTemplate = jumpUrl.includes('{{')
      ? dynamicString(jumpUrl, data) || ''
      : jumpUrl;
    if (!fromTemplate) return '';
    if (fromTemplate.startsWith('/dashboard/game/')) return fromTemplate;
    return `/dashboard/game/${gameCode}${fromTemplate}`;
  }, [
    financeJumpUrlsByGranularity,
    financeJumpUrlsByGranularityGlobal,
    filterVal?.date_type,
    jumpUrl,
    data,
    gameCode,
  ]);

  const jumpToLink = () => {
    if (!resolvedJumpUrl) return;
    if (jumpType === 'window') {
      window.open(resolvedJumpUrl, '_blank');
      return;
    }
    jumpDetail(
      resolvedJumpUrl,
      buildJumpDetailFormValue(filterVal as Record<string, unknown>, timeType) as Parameters<
        typeof jumpDetail
      >[1],
    );
  };

  const { trendData, xAxisData } = useMemo(() => {
    const trendData: any = {};
    const xAxisData = keyMetricsTrend?.map((item: any) => item?.date);
    if (keyMetricsTrend) {
      effectiveMetricsList?.map((v: any) => {
        const currentList: string[] = [];
        const lastList: string[] = [];
        keyMetricsTrend?.map((item: any) => {
          currentList?.push(item?.metrics?.[v]?.value);
          lastList?.push(item?.metrics?.[v]?.last_value);
        });
        trendData[v] = [currentList, lastList];
      });
    }
    return {
      xAxisData,
      trendData,
    };
  }, [keyMetricsTrend, effectiveMetricsList]);

  const dataSource = useMemo(() => {
    let newData: any = {};
    componentMetrics?.map((item: any) => {
      const { metricKey, enName, cnName, rangeEnName, rangeCnName, rainbowKey, type, decimal, formatRule, valueFormatRule, icon, hasChart = true, explainEn = '', explainCn = '', endEnName, endCnName } = item;
      const dataItem = metricsData?.find((v: { metrics_name: string; }) => v?.metrics_name === metricKey) || {};
      const { metrics_name, value, time: _itemTime, ...other } = dataItem;
      const itemData = _itemTime ? { ...data, _cardItemTime: String(_itemTime) } : data;
      let name = getName(isEN, dynamicString(enName, itemData), dynamicString(cnName, itemData)) || '';
      // 多天名称
      const rangeName = getName(isEN, dynamicString(rangeEnName, itemData), dynamicString(rangeCnName, itemData)) || '';
      const endName = getName(isEN, dynamicString(endEnName, itemData), dynamicString(endCnName, itemData)) || '';
      if (isChange) {
        name = rangeName || name;
      }
      const explain = getLanguage(isEN, dynamicString(explainEn, itemData), dynamicString(explainCn, itemData)) || '';
      // 兼容老的解释, 后续配置更新完可删除
      const explainOld = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
      const series = formatSeries(trendData?.[metrics_name] || [], timeType?.toUpperCase(), formatMessage);
      const newItem = {
        ...item,
        name,
        icon,
        metrics_name,
        endName,
        value: (valueFormatRule || formatRule) ? dataFormat({ value, formatRule: dynamicString(valueFormatRule || formatRule, itemData) }) : getDecimalFormateStr(type, value, metrics_name, decimal),
        bubbleData: { ...other },
        explain: (explain || explainOld)?.replaceAll('\\n', '<br/>'),
        xAxisData,
        series,
        formatRule: dynamicString(formatRule, itemData),
        dimension,
        defaultSelectedList,
      };
      if (hasChart) {
        newData = { ...newItem, xAxisData, series, list: [] };
        return;
      }
      newData?.list?.push(newItem);
    });
    return newData || {};
  }, [componentMetrics, xAxisData, timeType, trendData, metricsData, isEN, data]);

  return (
    <ACard url={resolvedJumpUrl}>
      <Card className={classNames('dbd-overlay-card overlay-card', className)} style={style} onClick={jumpToLink}>
        <Card.Body
          className={isClick ? 'overlay-card-click' : ''}
          title={
            <div className='dbd-overlay-card-container' style={{ paddingBottom: propsHasVs && hasVs ? 15 : 0 }}>
              <MetricCardTitle dataSource={dataSource} isDark={isDark} jumpUrl={resolvedJumpUrl} />
              <VsBenchmarkBtn hasVs={propsHasVs && hasVs} benchmarkConfig={benchmarkConfig} componentMetric={componentMetrics?.[0]} {...restProps} />
            </div>
          }
        >
          {isRealTime && dataSource?.bubbleData?.dod?.this_time && (
            <div className={"cart-margin-text"}>{moment(dataSource?.bubbleData?.dod?.this_time).format('HH:mm')}</div>
          )}
          <div className="overlay-card-flex">
            <div className="overlay-card-flex-lf">
              <MetricCardItem isMain isChange={isChange} dataSource={dataSource} timeType={timeType} />
              {hasChart && <MetricCardChartItem isPlatform={true} dataSource={dataSource} timeType={timeType} />}
            </div>
            <div className="overlay-card-flex-rt">
              <PlatformCardItem dataSource={dataSource} platFormData={platFormData} showShare={showShare} breakdownIconConfig={breakdownIconConfig} gameList={gameList} breakdownTooltipMap={breakdownTooltipMap}></PlatformCardItem>
            </div>
          </div>
        </Card.Body>
      </Card>
    </ACard>
  );
}